import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { useTooltip } from '../../hooks';
import { keyCode, KEYBOARD_SHORTCUT } from '../../helpers';
import { Icon, Button, Label } from '../';
import { ValidationMessage } from '../ValidationMessage';
import { InlineSelect } from './components';
import './Input.css';

/**
 * Text, password and file input with an optional label, inline validation and button affordances.
 *
 * @param {string}   name                        - Field name, passed back on every onChange.
 * @param {string}   type                        - Input type: 'text', 'password', 'file', or any
 *                                                 other the browser accepts.
 * @param {string}   [placeholder]               - Placeholder text; suppressed when labelAsPlaceholder.
 * @param {string}   [accept]                    - Accepted file types, for type 'file'.
 * @param {string}   [label]                     - Label text.
 * @param {boolean}  [labelAsPlaceholder]        - Renders the label inside the field instead of above it.
 * @param {*}        value                       - Current value; the file name for type 'file'.
 * @param {boolean}  [disabled=false]            - Disables the field and hides every button.
 * @param {boolean}  [isNullable=false]          - Shows a clear button. Without onClear it appears only
 *                                                 while the field has a value, and empties it.
 * @param {Function} onChange                    - Called with { name, value } on every change.
 * @param {Function} [onClear]                   - Called instead of emptying the field when the clear
 *                                                 button is pressed, and makes that button appear even
 *                                                 while the field is empty. For a field whose value does
 *                                                 not say whether there is anything to clear, such as a
 *                                                 password the server never sends back.
 * @param {string}   [clearTooltip]              - Tooltip on the clear button. Worth setting wherever
 *                                                 clearing does something other than empty the field.
 * @param {Function} [onBlur]                    - Called when the field loses focus.
 * @param {Function} [onFocus]                   - Called when the field gains focus.
 * @param {Function} [onKeyDown]                 - Called with (event, value) on key down.
 * @param {string}   [autoComplete='off']        - Browser autocomplete hint.
 * @param {boolean|string} [required=false]      - Marks the field required.
 * @param {string}   [className]                 - Extra classes on the wrapper.
 * @param {string}   [inputClassName]            - Extra classes on the input element itself.
 * @param {string}   [icon]                      - Icon rendered inside the field.
 * @param {string}   [keyboardShortcut]          - Shortcut hint shown while the field is empty.
 * @param {boolean}  [iconAsButton=false]        - Treats the icon as a toggle that collapses the field.
 * @param {boolean}  [showCapsLockWarning=true]  - Warns while Caps Lock is on, for type 'password'.
 * @param {boolean}  [showTogglePasswordVisibility=true] - Shows the reveal button, for type 'password'.
 * @param {{ valid: boolean|string, message: string, tooltip?: string, tooltipBody?: * }} [validation]
 *                                               - Validation state, rendered as an icon and a message.
 * @param {string}   [title]                     - Tooltip text on the label.
 * @param {string}   [displayNameCode]           - Display-name code the label resolves its text from.
 * @param {'left'}   [labelPosition]             - Places the label beside the field rather than above it.
 * @param {boolean}  [withSelect=false]          - Renders the inline selects described by selectProps.
 * @param {Object[]} [selectProps]               - Props for each inline select, when withSelect.
 * @param {boolean}  [preventEnterDefault=true]  - Swallows Enter, so it does not submit the form.
 * @param {boolean}  [isOptional=false]          - Renders an "(optional)" hint next to the label.
 * @param {React.Ref} ref                        - Forwarded to the input element.
 * @returns {JSX.Element}
 */
const _Input = (
  {
    name,
    type,
    placeholder,
    accept,
    label,
    labelAsPlaceholder,
    value,
    disabled = false,
    isNullable = false,
    onChange,
    onClear,
    clearTooltip,
    onBlur,
    onFocus,
    onKeyDown,
    autoComplete = 'off',
    required = false,
    className,
    inputClassName,
    icon,
    keyboardShortcut,
    iconAsButton = false,
    showCapsLockWarning = true,
    showTogglePasswordVisibility = true,
    validation,
    title,
    displayNameCode,
    labelPosition,
    withSelect = false,
    selectProps,
    preventEnterDefault = true,
    isOptional = false,
  },
  ref
) => {
  const { showTooltip, hideTooltip } = useTooltip();

  const [inputType, setInputType] = useState(type);
  const [isActive, setIsActive] = useState(false);
  const [expanded, setExpanded] = useState(value ? true : false);
  const [capsLock, setCapsLock] = useState(false);

  const tmpRef = useRef();
  const inputRef = ref || tmpRef;
  const inputSelectRef = useRef([]);
  const inputFileRef = useRef();

  useEffect(() => {
    if (!value || value === '') {
      setIsActive(false);
    } else {
      setIsActive(true);
    }
  }, [value]);

  useEffect(() => {
    if (disabled) {
      togglePasswordVisibility('password');
    }
  }, [disabled]);

  // A field whose value is always empty cannot be cleared by emptying it -- a stored password is
  // never sent to the browser, so the input shows '' whether or not one exists. onClear lets the
  // parent say what clearing means for such a field; without it, clearing is emptying.
  const clearInput = () => {
    onClear ? onClear() : onChange({ value: '', name: name });
    inputRef.current && inputRef.current.focus();
  };

  const handleExpand = el => {
    onFocus && onFocus(el);

    inputRef.current.focus();
    setExpanded(true);
  };
  const handleBlur = element => {
    if (
      iconAsButton &&
      value === '' &&
      inputSelectRef.current.findIndex(el => el === element.relatedTarget) < 0
    ) {
      setExpanded(false);
    }
    setCapsLock(false);
    onBlur && onBlur(element);
  };
  const handleKeyDown = keyEvent => {
    if (inputType === 'password' && showCapsLockWarning) {
      if (keyEvent.getModifierState('CapsLock')) {
        setCapsLock(true);
      } else {
        setCapsLock(false);
      }
    }

    if (preventEnterDefault && keyEvent.keyCode === keyCode.ENTER) {
      keyEvent.preventDefault();
    }

    onKeyDown && onKeyDown(keyEvent, value);
  };

  const handleClick = () => {
    inputFileRef.current.click();
  };

  const togglePasswordVisibility = state => {
    if (type === 'password' && showTogglePasswordVisibility) {
      setInputType(prev => {
        return state || (prev === 'password' ? 'text' : 'password');
      });
    }
  };

  const getFileName = value => {
    return value ? value.split(/\\|\//).slice(-1) : 'No file selected';
  };
  const labelElement = (
    <Label
      label={label}
      className={isActive ? 'active' : ''}
      htmlFor={name}
      displayNameCode={displayNameCode}
      title={title}
      disabled={disabled}
      isOptional={isOptional}
    />
  );

  return (
    <div
      className={
        'CustomInput' +
        (className ? ' ' + className : '') +
        (disabled ? ' disabled' : '') +
        (labelAsPlaceholder ? ' label-as-placeholder' : '') +
        (icon ? ' with-icon' : '') +
        (iconAsButton && !expanded ? ' collapsed' : '') +
        (validation && !validation.valid ? ' has-error' : '') +
        (labelPosition ? ' label-position-' + labelPosition : '') +
        (inputType === 'password' && showTogglePasswordVisibility ? '' : ' show-password') +
        (withSelect ? ' with-select' : '')
      }
    >
      {/*
      <Label
        label={label}
        className={isActive ? 'active' : ''}
        htmlFor={name}
        title={title}
        disabled={disabled}
      />
      */}

      {inputType === 'file' ? (
        <div className="browse-button">
          <Button value="Browse..." onClick={handleClick} />

          <div className={'browse-button-file-name' + (value ? '' : ' no-file')}>
            {getFileName(value)}
            <div className="button-clear-input" onClick={clearInput} tabIndex={1}>
              <div className="button-clear-input-icon"></div>
            </div>
          </div>

          <div className="input-file-wrapper">
            <input
              type="file"
              name={name}
              ref={inputFileRef}
              //value={value} // Do not set this for files, otherwise an "An attempt was made to use an object that is not, or is no longer, usable" error will be thrown when the value is set.
              onChange={onChange}
              accept={accept}
            />
          </div>
        </div>
      ) : (
        <>
          {labelPosition === 'left' && labelElement}
          <div className="input-container-wrapper">
            <div className="input-container" onClick={() => inputRef.current.focus()}>
              {labelPosition !== 'left' && labelElement}
              {labelPosition === 'left' && isOptional && !isActive && (
                <span
                  className="is-optional"
                  onMouseEnter={() => showTooltip({ title: 'This field is optional to fill in' })}
                  onMouseLeave={hideTooltip}
                >
                  optional
                </span>
              )}

              <div className="input-content" onClick={handleExpand}>
                {icon && (
                  <div className="input-icon">
                    <Icon name={icon} />
                  </div>
                )}
                <input
                  className={'input' + (inputClassName ? ' ' + inputClassName : '')}
                  type={inputType || type}
                  ref={inputRef}
                  name={name}
                  placeholder={labelAsPlaceholder ? '' : placeholder}
                  value={value}
                  onChange={onChange}
                  onKeyDown={handleKeyDown}
                  disabled={disabled}
                  autoComplete={autoComplete}
                  onBlur={handleBlur}
                  onFocus={handleExpand}
                  required={required || required === 'required' ? 'required' : false}
                />
                {((isNullable && (value !== '' || onClear)) ||
                  validation ||
                  capsLock ||
                  withSelect ||
                  keyboardShortcut ||
                  type === 'password') &&
                  !disabled && (
                    <div className="button-container">
                      {keyboardShortcut && !value && (
                        <div className="keyboard-shortcut">
                          {KEYBOARD_SHORTCUT[keyboardShortcut]}
                        </div>
                      )}
                      {capsLock && showCapsLockWarning && (
                        <div
                          className="input-button caps-lock-warning"
                          onMouseEnter={() => showTooltip({ title: 'Caps Lock is ON!' })}
                          onMouseLeave={hideTooltip}
                        >
                          <Icon name="caps_lock" />
                        </div>
                      )}
                      {validation && (
                        <div
                          className={
                            'input-button validation-icon' +
                            (validation.valid === true
                              ? ' valid'
                              : !validation.valid
                                ? ' error'
                                : ' ' + validation.valid)
                          }
                          onMouseEnter={() =>
                            showTooltip({
                              title: validation.message,
                              bodyText: validation.tooltip,
                              tooltipBody: validation.tooltipBody,
                            })
                          }
                          onMouseLeave={hideTooltip}
                        ></div>
                      )}
                      {isNullable && (value !== '' || onClear) && (
                        <div
                          className="input-button button-clear-input"
                          onClick={clearInput}
                          onMouseEnter={
                            clearTooltip ? () => showTooltip({ title: clearTooltip }) : undefined
                          }
                          onMouseLeave={clearTooltip ? hideTooltip : undefined}
                          tabIndex={1}
                        >
                          <div className="button-clear-input-icon"></div>
                        </div>
                      )}
                      {type === 'password' && showTogglePasswordVisibility && (
                        <div
                          className="input-button button-show-password"
                          onMouseEnter={() =>
                            showTooltip({
                              title: inputType === 'password' ? 'Show password' : 'Hide password',
                            })
                          }
                          onMouseLeave={hideTooltip}
                          onClick={() => togglePasswordVisibility()}
                        >
                          <Icon name="toggle_visibility" />
                        </div>
                      )}
                    </div>
                  )}
              </div>
            </div>
            {withSelect && (
              <div className="select-buttons">
                {selectProps.map((props, i) => (
                  <div
                    className="input-container"
                    key={i}
                    onClick={() => inputSelectRef.current[i].focus()}
                  >
                    {/*props.label && 
              <Label
            label={props.label}
            className={isActive ? 'active' : ''}
            htmlFor={name}
            title={props.tooltip}
            disabled={disabled}
            isOptional={props.isOptional}
          />  
          */}

                    <div className="input-content">
                      <InlineSelect
                        ref={el => (inputSelectRef.current[i] = el)}
                        name={props.name}
                        value={props.value}
                        label={props.label}
                        tooltip={props.tooltip}
                        options={props.options}
                        onChange={props.onChange}
                        isMulti={props.isMulti}
                        onBlur={handleBlur}
                        inputRef={inputRef}
                        disabled={disabled}
                        isOptional={props.isOptional}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      <ValidationMessage validation={validation} />
    </div>
  );
};

export const Input = forwardRef(_Input);
